import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import {
	adminAPI, horseAPI, transportAPI, productAPI,
	schoolAPI, stableAPI, trainerAPI, eventAPI, vetAPI,
	reportAPI, verificationAPI, ratingAPI,
} from '@/services/api';
import {
  Users, FileText, Tag, Shield, Trash2, Plus, X,
  CheckCircle, XCircle, Truck, Building2, MapPin,
  Image, Video, Crown, Save, Pencil, EuroIcon,
	Power, PowerOff, Flag, ExternalLink, AlertTriangle,
	GraduationCap, Award, Star, CalendarDays, Stethoscope,
	ShieldOff, ShieldUser, Zap, ZapOff
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import FAQAdminManager from '@/components/FAQAdminManager';
import SuggestionsAdminManager from '@/components/SuggestionsAdminManager';
import LegalAdminManager from '@/components/LegalAdminManager';
import InboxAdminManager from '@/components/InboxAdminManager';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Textarea } from "@/components/ui/textarea";
import { resolveUrl, resolveLabel } from "@/utils/adUtil";

const LANGS = ['en', 'pt', 'es', 'fr', 'de', 'pl'];

export default function AdminPage() {
  const { t, i18n } = useTranslation();
  const { user, isAdmin, loading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [horseAds, setHorseAds] = useState([]);
  const [productAds, setProductAds] = useState([]);
	const [transportAds, setTransportAds] = useState([]);
	const [schoolAds, setSchoolAds] = useState([]);
	const [stableAds, setStableAds] = useState([]);
	const [trainerAds, setTrainerAds] = useState([]);
	const [eventAds, setEventAds] = useState([]);
	const [vetAds, setVetAds] = useState([]);
  const [breeds, setBreeds] = useState([]);
	const [coats, setCoats] = useState([]);
  const [categories, setCategories] = useState([]);
  const [verifications, setVerifications] = useState([]);
  const [settings, setSettings] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [adsDist, setAdsDist] = useState([]);
  const [newBreed, setNewBreed] = useState('');
	const [newCoat, setNewCoat] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [editingBreed, setEditingBreed] = useState(null);
	const [editingCoat, setEditingCoat] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [breedTranslations, setBreedTranslations] = useState({});
	const [popularBreed, setPopularBreed] = useState(false);
	const [coatTranslations, setCoatTranslations] = useState({});
	const [coatDescriptionTranslations, setCoatDescriptionTranslations] = useState({});
  const [catTranslations, setCatTranslations] = useState({});
  const [settingsForm, setSettingsForm] = useState({});
  const [settingsSaving, setSettingsSaving] = useState(false);
	const [reports, setReports] = useState([]);
	const [reportStats, setReportStats] = useState({ open: 0, resolved: 0, dismissed: 0 });
	const [reportFilter, setReportFilter] = useState('open');
	const [rejectDialog, setRejectDialog] = useState({ open: false, id: null, reason: '' });
	const [ratingReports, setRatingReports] = useState([]);
	const [ratingReportStats, setRatingReportStats] = useState({ open: 0, resolved: 0, dismissed: 0 });
	const [ratingReportFilter, setRatingReportFilter] = useState('open');

  useEffect(() => {
    if (loading) return;
    if (!user || !isAdmin) { navigate('/'); return; }
    loadAll();
  }, [user, isAdmin, navigate, loading]);

	const mergeAds = (ads, pendingAds) => {
		const publishedAds = ads.map((ad) => ({
			...ad,
			pendingPayment: pendingAds.filter(pend => ad.id === pend.id).length > 0,
		}));
		const publishedSet = new Set(ads.map((ad) => ad.id));
		const unpublishedADs = pendingAds.filter(pend => !publishedSet.has(pend.id)).map((pend) => ({
			...pend,
			pendingPayment: true,
		}))
		return [...publishedAds, ...unpublishedADs];
	}

  const loadAll = async () => {
    try {
      const [
				statsPromise,
				usersPromise,
				horseAdsPromise,
				pendingHorsAdsPromise,
				productAdsPromise,
				pendingProductAdsPromise,
				transportAdsPromise,
				pendingTransportAdsPromise,
				schoolAdsPromise,
				pendingSchoolAdsPromise,
				stableAdsPromise,
				pendingStableAdsPromise,
				trainerAdsPromise,
				pendingTrainerAdsPromise,
				eventAdsPromise,
				pendingEventAdsPromise,
				vetAdsPromise,
				pendingVetAdsPromise,
				breedsPromise,
				coatsPromise,
				categoriesPromise,
				verificationsPromise,
				settingsPromise,
				revenuePromise,
				adDistributionPromise,
				reportsPromise,
				reportStatsPromise,
				ratingReportPromise,
				ratingReportStatsPromise,
			] = await Promise.all([
        adminAPI.stats(),
				adminAPI.users({}),
        adminAPI.ads({ ad_type: 'horse', status: 'all' }),
				adminAPI.pendingAds({ ad_type: 'horse', status: 'all' }),
        adminAPI.ads({ ad_type: 'product', status: 'all' }),
				adminAPI.pendingAds({ ad_type: 'product', status: 'all' }),
				adminAPI.ads({ ad_type: 'transport', status: 'all' }),
				adminAPI.pendingAds({ ad_type: 'transport', status: 'all' }),
				adminAPI.ads({ ad_type: 'school', status: 'all' }),
				adminAPI.pendingAds({ ad_type: 'school', status: 'all' }),
				adminAPI.ads({ ad_type: 'stable', status: 'all' }),
				adminAPI.pendingAds({ ad_type: 'stable', status: 'all' }),
				adminAPI.ads({ ad_type: 'trainer', status: 'all' }),
				adminAPI.pendingAds({ ad_type: 'trainer', status: 'all' }),
				adminAPI.ads({ ad_type: 'event', status: 'all' }),
				adminAPI.pendingAds({ ad_type: 'event', status: 'all' }),
				adminAPI.ads({ ad_type: 'vet', status: 'all' }),
				adminAPI.pendingAds({ ad_type: 'vet', status: 'all' }),
        adminAPI.breeds(),
				adminAPI.coats(),
				adminAPI.categories(),
        adminAPI.verifications('all').catch(() => ({ data: [] })),
        adminAPI.getSettings().catch(() => ({ data: null })),
        adminAPI.revenue().catch(() => ({ data: null })),
        adminAPI.adsDistribution().catch(() => ({ data: [] })),
				reportAPI.listAdmin({ status: 'open' }).catch(() => ({ data: { items: [] } })),
				reportAPI.stats().catch(() => ({ data: { open: 0, resolved: 0, dismissed: 0 } })),
				ratingAPI.adminReports({ status: 'open' }).catch(() => ({ data: { items: [] } })),
				ratingAPI.adminStats().catch(() => ({ data: { open: 0, resolved: 0, dismissed: 0 } })),
      ]);
      setStats(statsPromise.data);
      setUsers(usersPromise.data.items || []);
      setHorseAds(mergeAds(horseAdsPromise.data.items || [], pendingHorsAdsPromise.data.items || []));
			setProductAds(mergeAds(productAdsPromise.data.items || [], pendingProductAdsPromise.data.items || []));
			setTransportAds(mergeAds(transportAdsPromise.data.items || [], pendingTransportAdsPromise.data.items || []));
			setSchoolAds(mergeAds(schoolAdsPromise.data.items || [], pendingSchoolAdsPromise.data.items || []));
			setStableAds(mergeAds(stableAdsPromise.data.items || [], pendingStableAdsPromise.data.items || []));
			setTrainerAds(mergeAds(trainerAdsPromise.data.items || [], pendingTrainerAdsPromise.data.items || []));
			setEventAds(mergeAds(eventAdsPromise.data.items || [], pendingEventAdsPromise.data.items || []));
			setVetAds(mergeAds(vetAdsPromise.data.items || [], pendingVetAdsPromise.data.items || []));
      setBreeds(breedsPromise.data || []);
			setCoats(coatsPromise.data || []);
      setCategories(categoriesPromise.data || []);
      setVerifications(verificationsPromise.data || []);
      if (settingsPromise.data) {
				setSettings(settingsPromise.data);
				setSettingsForm(settingsPromise.data);
			}
      if (revenuePromise.data) {
				setRevenue(revenuePromise.data);
			}
      setAdsDist(adDistributionPromise.data || []);
			setReports(reportsPromise.data?.items || []);
			setReportStats(reportStatsPromise.data || { open: 0, resolved: 0, dismissed: 0 });
			setRatingReports(ratingReportPromise.data?.items || []);
			setRatingReportStats(ratingReportStatsPromise.data || { open: 0, resolved: 0, dismissed: 0 });
    } catch {}
  };

	const toggleBlock = async (userId, blocked) => {
		try {
			await adminAPI.updateUser(userId, { is_blocked: !blocked });
			toast.success(blocked ? t('admin.unblock_user') : t('admin.block_user'));
			await loadAll();
		} catch {
			toast.error(t('common.error'));
		}
	};

	const toggleAdmin = async (userId, currentRole) => {
		const newRole = currentRole === 'admin' ? 'user' : 'admin';
		if (!window.confirm(t('admin.confirm_role_change', { role: newRole }))) return;
		try {
			await adminAPI.updateUser(userId, { role: newRole });
			toast.success(t('admin.role_changed', { role: newRole }));
			await loadAll();
		} catch {
			toast.error(t('common.error'));
		}
	};

	const deactivateAd = async (adId, type) => {
		try {
			await adminAPI.deactivateAd(adId, type);
			toast.success(t('admin.deactivate'));
			await loadAll();
		} catch {
			toast.error(t('common.error'));
		}
	};

	const reactivateAd = async (adId, type) => {
		try {
			await adminAPI.reactivateAd(adId, type);
			toast.success(t('admin.activate'));
			await loadAll();
		} catch {
			toast.error(t('common.error'));
		}
	};

	const deleteHorseAd = async (adId) => {
		try {
			await adminAPI.deleteHorseAd(adId);
			toast.success(t('common.delete'));
			await loadAll();
		} catch {
			toast.error(t('common.error'));
		}
	};

	const deleteProductAd = async (adId) => {
		try {
			await adminAPI.deleteProductAd(adId);
			toast.success(t('common.delete'));
			await loadAll();
		} catch {
			toast.error(t('common.error'));
		}
	};

	const deleteTransportAd = async (adId) => {
		try {
			await adminAPI.deleteTransportAd(adId);
			toast.success(t('common.delete'));
			await loadAll();
		} catch {
			toast.error(t('common.error'));
		}
	};

	const deleteSchoolAd = async (adId) => {
		try {
			await adminAPI.deleteSchoolAd(adId);
			toast.success(t('common.delete'));
			await loadAll();
		} catch {
			toast.error(t('common.error'));
		}
	};

	const deleteStableAd = async (adId) => {
		try {
			await adminAPI.deleteStableAd(adId);
			toast.success(t('common.delete'));
			await loadAll();
		} catch {
			toast.error(t('common.error'));
		}
	};

	const deleteTrainerAd = async (adId) => {
		try {
			await adminAPI.deleteTrainerAd(adId);
			toast.success(t('common.delete'));
			await loadAll();
		} catch {
			toast.error(t('common.error'));
		}
	};

	const deleteEventAd = async (adId) => {
		try {
			await adminAPI.deleteEventAd(adId);
			toast.success(t('common.delete'));
			await loadAll();
		} catch {
			toast.error(t('common.error'));
		}
	};

	const deleteVetAd = async (adId) => {
		try {
			await adminAPI.deleteVetAd(adId);
			toast.success(t('common.delete'));
			await loadAll();
		} catch {
			toast.error(t('common.error'));
		}
	};

	const deletePendingHorseAd = async (id) => {
		try {
			await horseAPI.deletePending(id);
			await loadAll();
			toast.success(t('common.ad_deleted'));
		} catch {
			toast.error(t('common.error'));
		}
	};

	const deletePendingProductAd = async (id) => {
		try {
			await productAPI.deletePending(id);
			await loadAll();
			toast.success(t('common.ad_deleted'));
		} catch {
			toast.error(t('common.error'));
		}
	};

	const deletePendingTransportAd = async (id) => {
		try {
			await transportAPI.deletePending(id);
			await loadAll();
			toast.success(t('common.ad_deleted'));
		} catch {
			toast.error(t('common.error'));
		}
	};

	const deletePendingSchoolAd = async (id) => {
		try {
			await schoolAPI.deletePending(id);
			await loadAll();
			toast.success(t('common.ad_deleted'));
		} catch {
			toast.error(t('common.error'));
		}
	};

	const deletePendingStableAd = async (id) => {
		try {
			await stableAPI.deletePending(id);
			await loadAll();
			toast.success(t('common.ad_deleted'));
		} catch {
			toast.error(t('common.error'));
		}
	};

	const deletePendingTrainerAd = async (id) => {
		try {
			await trainerAPI.deletePending(id);
			await loadAll();
			toast.success(t('common.ad_deleted'));
		} catch {
			toast.error(t('common.error'));
		}
	};

	const deletePendingEventAd = async (id) => {
		try {
			await eventAPI.deletePending(id);
			await loadAll();
			toast.success(t('common.ad_deleted'));
		} catch {
			toast.error(t('common.error'));
		}
	};

	const deletePendingVetAd = async (id) => {
		try {
			await vetAPI.deletePending(id);
			await loadAll();
			toast.success(t('common.ad_deleted'));
		} catch {
			toast.error(t('common.error'));
		}
	};

	const addBreed = async () => {
		if (!newBreed.trim()) return;
		try {
			await adminAPI.createBreed({ name: newBreed, translations: {} });
			setNewBreed('');
			toast.success(t('admin.add_breed'));
			await loadAll();
		} catch {
			toast.error(t('common.error'));
		}
	};

	const deleteBreed = async (id) => {
		try {
			await adminAPI.deleteBreed(id);
			toast.success(t('common.delete'));
			await loadAll();
		} catch {
		}
	};

	const addCoat = async () => {
		if (!newCoat.trim()) return;
		try {
			await adminAPI.createCoat(
				{
					name: newCoat,
					translations: {},
					descriptionTranslations: {}
				}
			);
			setNewCoat('');
			toast.success(t('admin.add_coat'));
			await loadAll();
		} catch {
			toast.error(t('common.error'));
		}
	};

	const deleteCoat = async (id) => {
		try {
			await adminAPI.deleteCoat(id);
			toast.success(t('common.delete'));
			await loadAll();
		} catch {
		}
	};

	const addCategory = async () => {
		if (!newCategory.trim()) return;
		try {
			await adminAPI.createCategory({ name: newCategory, translations: {} });
			setNewCategory('');
			toast.success(t('admin.add_category'));
			await loadAll();
		} catch {
			toast.error(t('common.error'));
		}
	};

	const deleteCategory = async (id) => {
		try {
			await adminAPI.deleteCategory(id);
			toast.success(t('common.delete'));
			await loadAll();
		} catch {
		}
	};

	const markAsFeatured = async (adId, type) => {
		try {
			await adminAPI.promoteToFeatured(adId, type);
			toast.success(t('admin.marked_featured'));
			await loadAll();
		} catch {
			toast.error(t('common.error'));
		}
	};

	const markAsStandard = async (adId, type) => {
		try {
			await adminAPI.makeAdStandard(adId, type);
			toast.success(t('admin.marked_standard'));
			await loadAll();
		} catch {
			toast.error(t('common.error'));
		}
	};

	const openBreedTranslations = (breed) => {
		setEditingBreed(breed);
		setPopularBreed(breed.popular);
		setBreedTranslations(breed.translations || {});
	};

	const saveBreedTranslations = async () => {
		try {
			await adminAPI.updateBreed(editingBreed.id, {
				name: editingBreed.name,
				popular: popularBreed,
				translations: breedTranslations
			});
			setEditingBreed(null);
			setPopularBreed(false);
			toast.success(t('common.success'));
			await loadAll();
		} catch {
			toast.error(t('common.error'));
		}
	};

	const openCoatTranslations = (coat) => {
		setEditingCoat(coat);
		setCoatTranslations(coat.translations || {});
		setCoatDescriptionTranslations(coat.descriptionTranslations || {});
	};

	const saveCoatTranslations = async () => {
		try {
			await adminAPI.updateCoat(
				editingCoat.id,
				{
					name: editingCoat.name,
					translations: coatTranslations,
					descriptionTranslations: coatDescriptionTranslations,
				}
			);
			setEditingCoat(null);
			toast.success(t('common.success'));
			await loadAll();
		} catch {
			toast.error(t('common.error'));
		}
	};

  const openCatTranslations = (cat) => {
    setEditingCategory(cat);
    setCatTranslations(cat.translations || {});
  };

	const saveCatTranslations = async () => {
		try {
			await adminAPI.updateCategory(
				editingCategory.id,
				{ name: editingCategory.name, translations: catTranslations }
			);
			setEditingCategory(null);
			toast.success(t('common.success'));
			await loadAll();
		} catch {
			toast.error(t('common.error'));
		}
	};

	const saveSettings = async () => {
		setSettingsSaving(true);
		try {
			const res = await adminAPI.updateSettings(settingsForm);
			setSettings(res.data);
			setSettingsForm(res.data);
			toast.success(t('common.success'));
		} catch {
			toast.error(t('common.error'));
		}
		setSettingsSaving(false);
	};

	const reviewVerification = async (verId, status, notes = '') => {
		try {
			await adminAPI.reviewVerification(verId, { status, notes });
			toast.success(t('admin.verification_updated', { status }));
			await loadAll();
		} catch {
			toast.error(t('common.error'));
		}
	};

	const loadReports = async (status) => {
		try {
			const params = status === 'all' ? {} : { status };
			const r = await reportAPI.listAdmin(params);
			setReports(r.data?.items || []);
		} catch { toast.error(t('common.error')); }
	};

	const changeReportFilter = async (newFilter) => {
		setReportFilter(newFilter);
		await loadReports(newFilter);
	};

	const reportTakeAction = async (reportId) => {
		if (!window.confirm(t('report.confirm_action'))) return;
		try {
			await reportAPI.action(reportId);
			toast.success(t('report.action_done'));
			const stats = await reportAPI.stats();
			setReportStats(stats.data);
			await loadReports(reportFilter);
		} catch { toast.error(t('common.error')); }
	};

	const reportResolve = async (reportId, status) => {
		try {
			await reportAPI.resolve(reportId, { status });
			toast.success(t('common.success'));
			const stats = await reportAPI.stats();
			setReportStats(stats.data);
			await loadReports(reportFilter);
		} catch { toast.error(t('common.error')); }
	};

	const loadRatingReports = async (status) => {
		try {
			const params = status === 'all' ? {} : { status };
			const r = await ratingAPI.adminReports(params);
			setRatingReports(r.data?.items || []);
		} catch { toast.error(t('common.error')); }
	};

	const changeRatingReportFilter = async (newFilter) => {
		setRatingReportFilter(newFilter);
		await loadRatingReports(newFilter);
	};

	const ratingReportDismiss = async (reportId) => {
		try {
			await ratingAPI.adminResolveReport(reportId, { status: 'dismissed' });
			toast.success(t('common.success'));
			const stats = await ratingAPI.adminStats();
			setRatingReportStats(stats.data);
			await loadRatingReports(ratingReportFilter);
		} catch { toast.error(t('common.error')); }
	};

	const ratingReportRemoveRating = async (ratingId) => {
		const reason = window.prompt(t('admin.rating_remove_reason_prompt'));
		if (reason === null) return;
		try {
			await ratingAPI.adminRemoveRating(ratingId, reason || '');
			toast.success(t('admin.rating_removed'));
			const stats = await ratingAPI.adminStats();
			setRatingReportStats(stats.data);
			await loadRatingReports(ratingReportFilter);
		} catch { toast.error(t('common.error')); }
	};

  const getTranslatedName = (item) => {
    const lang = i18n.language;
    if (lang !== 'en' && item.translations?.[lang]) return item.translations[lang];
    return item.name;
  };

  if (loading || !isAdmin) return null;

  const maxBar = adsDist.length > 0 ? Math.max(...adsDist.map(d => d.count)) : 1;

  return (
    <div className="pt-20 md:pt-24 min-h-screen" data-testid="admin-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center gap-3 mb-8">
          <Shield className="w-8 h-8 text-[#1A3C34]" />
          <h1 className="text-3xl font-medium text-[#1A3C34] font-['Playfair_Display']">
						{t('admin.title')}
					</h1>
        </div>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4 mb-8" data-testid="admin-stats">
            {[
              { label: t('admin.users'), value: stats.users, icon: Users },
              { label: t('admin.horse_ads'), value: stats.horse_ads, icon: FileText },
              { label: t('admin.product_ads'), value: stats.product_ads, icon: Tag },
							{ label: t('admin.transport_ads'), value: stats.transport_ads, icon: Truck },
							{ label: t('admin.school_ads'), value: stats.riding_schools, icon: GraduationCap },
							{ label: t('admin.stable_ads'), value: stats.stable_ads, icon: Building2 },
							{ label: t('admin.trainer_ads'), value: stats.trainer_ads, icon: Award },
							{ label: t('admin.event_ads'), value: stats.event_ads, icon: CalendarDays },
							{ label: t('admin.vet_ads'), value: stats.vet_ads, icon: Stethoscope },
              { label: t('admin.messages'), value: stats.messages, icon: FileText },
              { label: t('admin.payments'), value: stats.payments, icon: EuroIcon },
              { label: t('admin.verifications'), value: stats.verifications_pending, icon: Shield },
            ].map((s, i) => (
              <div key={i} className="bg-white border border-[#E6E2D6] rounded-sm p-4" data-testid={`stat-card-${i}`}>
                <s.icon className="w-5 h-5 text-[#8B4513] mb-2" />
                <p className="text-2xl font-bold text-[#1A3C34]">{s.value}</p>
                <p className="text-[10px] text-[#666055] uppercase tracking-wider mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        )}

        <Tabs defaultValue="users" className="space-y-6">
					<TabsList className="bg-white border border-[#E6E2D6] p-1 rounded-sm h-auto flex flex-nowrap justify-start
							md:flex-wrap md:justify-center overflow-x-auto md:overflow-visible gap-1 -mx-4 px-4 md:mx-0 md:px-1 scrollbar-hide">
						<TabsTrigger value="users" data-testid="admin-tab-users"
												 className="shrink-0 rounded-sm data-[state=active]:bg-[#1A3C34] data-[state=active]:text-white
												 		px-3 py-1.5 text-sm md:px-6 md:py-2 md:text-base whitespace-nowrap">
							{ t('admin.users') }
						</TabsTrigger>
						<TabsTrigger value="horse_ads" data-testid="admin-tab-horse-ads"
												 className="shrink-0 rounded-sm data-[state=active]:bg-[#1A3C34] data-[state=active]:text-white
												 		px-3 py-1.5 text-sm md:px-6 md:py-2 md:text-base whitespace-nowrap">
							{ t('admin.horse_ads') }
						</TabsTrigger>
						<TabsTrigger value="product_ads" data-testid="admin-tab-product-ads"
												 className="shrink-0 rounded-sm data-[state=active]:bg-[#1A3C34] data-[state=active]:text-white
												 		px-3 py-1.5 text-sm md:px-6 md:py-2 md:text-base whitespace-nowrap">
							{ t('admin.product_ads') }
						</TabsTrigger>
						<TabsTrigger value="transport_ads" data-testid="admin-tab-transport-ads"
												 className="shrink-0 rounded-sm data-[state=active]:bg-[#1A3C34] data-[state=active]:text-white
												 		px-3 py-1.5 text-sm md:px-6 md:py-2 md:text-base whitespace-nowrap">
							{t('admin.transport_ads')}
						</TabsTrigger>
						<TabsTrigger value="school_ads" data-testid="admin-tab-school-ads"
												 className="shrink-0 rounded-sm data-[state=active]:bg-[#1A3C34] data-[state=active]:text-white
												 		px-3 py-1.5 text-sm md:px-6 md:py-2 md:text-base whitespace-nowrap">
							{t('admin.school_ads')}
						</TabsTrigger>
						<TabsTrigger value="stable_ads" data-testid="admin-tab-stable-ads"
												 className="shrink-0 rounded-sm data-[state=active]:bg-[#1A3C34] data-[state=active]:text-white
												 		px-3 py-1.5 text-sm md:px-6 md:py-2 md:text-base whitespace-nowrap">
							{t('admin.stable_ads')}
						</TabsTrigger>
						<TabsTrigger value="trainer_ads" data-testid="admin-tab-trainer-ads"
												 className="shrink-0 rounded-sm data-[state=active]:bg-[#1A3C34] data-[state=active]:text-white
												 		px-3 py-1.5 text-sm md:px-6 md:py-2 md:text-base whitespace-nowrap">
							{t('admin.trainer_ads')}
						</TabsTrigger>
						<TabsTrigger value="event_ads" data-testid="admin-tab-event-ads"
												 className="shrink-0 rounded-sm data-[state=active]:bg-[#1A3C34] data-[state=active]:text-white
												 		px-3 py-1.5 text-sm md:px-6 md:py-2 md:text-base whitespace-nowrap">
							{t('admin.event_ads')}
						</TabsTrigger>
						<TabsTrigger value="vet_ads" data-testid="admin-tab-vet-ads"
												 className="shrink-0 rounded-sm data-[state=active]:bg-[#1A3C34] data-[state=active]:text-white
												 		px-3 py-1.5 text-sm md:px-6 md:py-2 md:text-base whitespace-nowrap">
							{t('admin.vet_ads')}
						</TabsTrigger>
						<TabsTrigger value="breeds" data-testid="admin-tab-breeds"
												 className="shrink-0 rounded-sm data-[state=active]:bg-[#1A3C34] data-[state=active]:text-white
												 		px-3 py-1.5 text-sm md:px-6 md:py-2 md:text-base whitespace-nowrap">
							{ t('admin.breeds') }
						</TabsTrigger>
						<TabsTrigger value="coats" data-testid="admin-tab-coats"
												 className="shrink-0 rounded-sm data-[state=active]:bg-[#1A3C34] data-[state=active]:text-white
												 		px-3 py-1.5 text-sm md:px-6 md:py-2 md:text-base whitespace-nowrap">
							{ t('admin.coats') }
						</TabsTrigger>
						<TabsTrigger value="categories" data-testid="admin-tab-categories"
												 className="shrink-0 rounded-sm data-[state=active]:bg-[#1A3C34] data-[state=active]:text-white
												 		px-3 py-1.5 text-sm md:px-6 md:py-2 md:text-base whitespace-nowrap">
							{ t('admin.categories') }
						</TabsTrigger>
						<TabsTrigger value="settings" data-testid="admin-tab-settings"
												 className="shrink-0 rounded-sm data-[state=active]:bg-[#1A3C34] data-[state=active]:text-white
												 		px-3 py-1.5 text-sm md:px-6 md:py-2 md:text-base whitespace-nowrap">
							{ t('admin.settings') }
						</TabsTrigger>
						<TabsTrigger value="revenue" data-testid="admin-tab-revenue"
												 className="shrink-0 rounded-sm data-[state=active]:bg-[#1A3C34] data-[state=active]:text-white
												 		px-3 py-1.5 text-sm md:px-6 md:py-2 md:text-base whitespace-nowrap">
							{ t('admin.revenue') }
						</TabsTrigger>
						<TabsTrigger value="distribution" data-testid="admin-tab-distribution"
												 className="shrink-0 rounded-sm data-[state=active]:bg-[#1A3C34] data-[state=active]:text-white
												 		px-3 py-1.5 text-sm md:px-6 md:py-2 md:text-base whitespace-nowrap">
							{ t('admin.distribution') }
						</TabsTrigger>
						<TabsTrigger value="verifications" data-testid="admin-tab-verifications"
												 className="shrink-0 rounded-sm data-[state=active]:bg-[#1A3C34] data-[state=active]:text-white
												 		px-3 py-1.5 text-sm md:px-6 md:py-2 md:text-base whitespace-nowrap">
							{ t('admin.verifications') } ({ verifications.filter(v => v.status === 'pending').length })
						</TabsTrigger>
						<TabsTrigger value="reports" data-testid="admin-tab-reports"
												 className="shrink-0 rounded-sm data-[state=active]:bg-[#1A3C34] data-[state=active]:text-white
												 		px-3 py-1.5 text-sm md:px-6 md:py-2 md:text-base whitespace-nowrap">
							{t('admin.reports')} ({reportStats.open})
						</TabsTrigger>
						<TabsTrigger value="rating_reports" data-testid="admin-tab-rating-reports"
												 className="shrink-0 rounded-sm data-[state=active]:bg-[#1A3C34] data-[state=active]:text-white
												 		px-3 py-1.5 text-sm md:px-6 md:py-2 md:text-base whitespace-nowrap">
							{t('admin.rating_reports')} ({ratingReportStats.open})
						</TabsTrigger>
						<TabsTrigger value="faqs" data-testid="admin-tab-faqs"
												 className="shrink-0 rounded-sm data-[state=active]:bg-[#1A3C34] data-[state=active]:text-white
												 		px-3 py-1.5 text-sm md:px-6 md:py-2 md:text-base whitespace-nowrap">
							{t('admin.faqs')}
						</TabsTrigger>
						<TabsTrigger value="suggestions" data-testid="admin-tab-suggestions"
												 className="shrink-0 rounded-sm data-[state=active]:bg-[#1A3C34] data-[state=active]:text-white
												 		px-3 py-1.5 text-sm md:px-6 md:py-2 md:text-base whitespace-nowrap">
							{t('admin.suggestions')}
						</TabsTrigger>
						<TabsTrigger value="legal" data-testid="admin-tab-legal"
												 className="shrink-0 rounded-sm data-[state=active]:bg-[#1A3C34] data-[state=active]:text-white
												 		px-3 py-1.5 text-sm md:px-6 md:py-2 md:text-base whitespace-nowrap">
							{t('admin.legal')}
						</TabsTrigger>
						<TabsTrigger value="inbox" data-testid="admin-tab-inbox"
												 className="shrink-0 rounded-sm data-[state=active]:bg-[#1A3C34] data-[state=active]:text-white
												 		px-3 py-1.5 text-sm md:px-6 md:py-2 md:text-base whitespace-nowrap">
							{t('admin.inbox')}
						</TabsTrigger>
					</TabsList>

          {/* ===== USERS ===== */}
					<TabsContent value="users">
						<div className="bg-white border border-[#E6E2D6] rounded-sm overflow-x-auto"
								 data-testid="admin-users-table">
							<table className="w-full text-sm">
								<thead className="bg-[#F2F0E9]">
								<tr>
									<th className="text-left p-3 text-xs font-semibold text-[#666055] uppercase">{ t('admin.name') }</th>
									<th className="text-left p-3 text-xs font-semibold text-[#666055] uppercase">{ t('admin.email') }</th>
									<th className="text-left p-3 text-xs font-semibold text-[#666055] uppercase">{ t('admin.role') }</th>
									<th	className="text-left p-3 text-xs font-semibold text-[#666055] uppercase">{ t('admin.status') }</th>
									<th	className="text-left p-3 text-xs font-semibold text-[#666055] uppercase">{ t('admin.actions') }</th>
								</tr>
								</thead>
								<tbody>
								{ users.map(u => (
									<tr key={ u.id } className="border-t border-[#E6E2D6]">
										<td className="p-3 font-medium text-[#1A3C34]">{ u.name }</td>
										<td className="p-3 text-[#666055]">{ u.email }</td>
										<td className="p-3">
											<Badge
												className={ `rounded-sm text-[10px] ${ u.role === 'admin' ? 'bg-[#1A3C34] text-white' : 'bg-[#F2F0E9] text-[#1A3C34]' }` }>
												{ u.role }
											</Badge>
										</td>
										<td className="p-3">
											<Badge
												className={ `rounded-sm text-[10px] ${ u.is_blocked ? 'bg-red-100 text-[#9B2C2C]' : 'bg-green-100 text-green-700' }` }>
												{ u.is_blocked ? t('admin.blocked') : t('admin.active') }
											</Badge>
										</td>
										<td className="p-3">
											<div className="flex gap-1">
												{ u.id !== user.id && (
													<>
														<Button onClick={ () => toggleBlock(u.id, u.is_blocked) } variant="outline" size="sm"
																		className="rounded-sm h-7 text-xs border-[#E6E2D6]"
																		data-testid={ `toggle-block-${ u.id }` }>
															{ u.is_blocked ? t('admin.unblock_user') : t('admin.block_user') }
														</Button>
														{ u.role === 'admin'  && (
															<Button onClick={ () => toggleAdmin(u.id, u.role) } variant="outline" size="sm"
																			className={ `rounded-sm h-7 text-xs border-red-200 text-[#9B2C2C]` }
																			data-testid={ `toggle-admin-${ u.id }` }>
																<ShieldOff
																	className="w-3 h-3 mr-1"/> { t('admin.remove_admin') }
															</Button>
														) }
														{ u.role !== 'admin'  && (
															<Button onClick={ () => toggleAdmin(u.id, u.role) } variant="outline" size="sm"
																			className={ `rounded-sm h-7 text-xs border-[#1A3C34] text-[#1A3C34]' }` }
																			data-testid={ `toggle-admin-${ u.id }` }>
																<ShieldUser
																	className="w-3 h-3 mr-1"/> { t('admin.make_admin') }
															</Button>
														) }
													</>
												) }
											</div>
										</td>
									</tr>
								)) }
								</tbody>
							</table>
						</div>
					</TabsContent>

          {/* ===== ADS ===== */}
					<TabsContent value="horse_ads">
						<AdTable ads={ horseAds } type="horse" onDeactivate={ deactivateAd } onReactivate={ reactivateAd }
										 onDeleteAd={ deleteHorseAd } onDeletePendingAd={ deletePendingHorseAd }
										 onMarkAsStandard={ markAsStandard } onMarkAsFeatured={ markAsFeatured } t={ t }/>
					</TabsContent>
          <TabsContent value="product_ads">
						<AdTable ads={productAds} type="product" onDeactivate={ deactivateAd } onReactivate={ reactivateAd }
										 onDeleteAd={ deleteProductAd } onDeletePendingAd={ deletePendingProductAd }
										 onMarkAsStandard={ markAsStandard } onMarkAsFeatured={ markAsFeatured } t={t} />
					</TabsContent>
					<TabsContent value="transport_ads">
						<AdTable ads={transportAds} type="transport" onDeactivate={ deactivateAd } onReactivate={ reactivateAd }
										 onDeleteAd={ deleteTransportAd } onDeletePendingAd={ deletePendingTransportAd }
										 onMarkAsStandard={ markAsStandard } onMarkAsFeatured={ markAsFeatured } t={t} />
					</TabsContent>
					<TabsContent value="school_ads">
						<AdTable ads={schoolAds} type="school" onDeactivate={deactivateAd} onReactivate={reactivateAd} t={t}
										 onDeleteAd={ deleteSchoolAd } onDeletePendingAd={ deletePendingSchoolAd }
										 onMarkAsStandard={ markAsStandard } onMarkAsFeatured={ markAsFeatured } />
					</TabsContent>
					<TabsContent value="stable_ads">
						<AdTable ads={stableAds} type="stable" onDeactivate={deactivateAd} onReactivate={reactivateAd} t={t}
										 onDeleteAd={ deleteStableAd } onDeletePendingAd={ deletePendingStableAd }
										 onMarkAsStandard={ markAsStandard } onMarkAsFeatured={ markAsFeatured } />
					</TabsContent>
					<TabsContent value="trainer_ads">
						<AdTable ads={trainerAds} type="trainer" onDeactivate={deactivateAd} onReactivate={reactivateAd} t={t}
										 onDeleteAd={ deleteTrainerAd } onDeletePendingAd={ deletePendingTrainerAd }
										 onMarkAsStandard={ markAsStandard } onMarkAsFeatured={ markAsFeatured } />
					</TabsContent>
					<TabsContent value="event_ads">
						<AdTable ads={eventAds} type="event" onDeactivate={deactivateAd} onReactivate={reactivateAd} t={t}
										 onDeleteAd={ deleteEventAd } onDeletePendingAd={ deletePendingEventAd }
										 onMarkAsStandard={ markAsStandard } onMarkAsFeatured={ markAsFeatured } />
					</TabsContent>
					<TabsContent value="vet_ads">
						<AdTable ads={vetAds} type="vet" onDeactivate={deactivateAd} onReactivate={reactivateAd} t={t}
										 onDeleteAd={ deleteVetAd } onDeletePendingAd={ deletePendingVetAd }
										 onMarkAsStandard={ markAsStandard } onMarkAsFeatured={ markAsFeatured } />
					</TabsContent>

          {/* ===== BREEDS ===== */}
					<TabsContent value="breeds">
						<div className="bg-white border border-[#E6E2D6] rounded-sm p-6" data-testid="admin-breeds">
							<div className="flex gap-2 mb-6">
								<Input value={ newBreed } onChange={ e => setNewBreed(e.target.value) }
											 placeholder={ t('admin.breed_name') }
											 className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-10"
											 data-testid="new-breed-input" onKeyDown={ e => e.key === 'Enter' && addBreed() }/>
								<Button onClick={ addBreed }
												className="bg-[#1A3C34] hover:bg-[#153029] text-white rounded-sm h-10 px-6"
												data-testid="add-breed-btn">
									<Plus className="w-4 h-4 mr-1"/>
									{ t('admin.add_breed') }
								</Button>
							</div>
							<div className="flex flex-wrap gap-2">
								{ breeds.map(b => (
									<div key={ b.id } className="flex items-center gap-2 bg-[#F2F0E9] px-3 py-2 rounded-sm"
											 data-testid={ `breed-tag-${ b.id }` }>
										<span className="text-sm text-[#1A3C34] font-medium">{ getTranslatedName(b) }</span>
										{ b.name !== getTranslatedName(b) &&
											<span className="text-[10px] text-[#666055]">({ b.name })
											</span>
										}
										<button onClick={ () => openBreedTranslations(b) }
														className="text-[#8B4513] hover:text-[#6F3610]"
														data-testid={ `edit-breed-${ b.id }` }>
											<Pencil className="w-3 h-3"/>
										</button>
										<button onClick={ () => deleteBreed(b.id) }
														className="text-[#9B2C2C] hover:text-red-700">
											<X className="w-3 h-3"/>
										</button>
									</div>
								)) }
							</div>
						</div>
					</TabsContent>

					{/* ===== COATS ===== */}
					<TabsContent value="coats">
						<div className="bg-white border border-[#E6E2D6] rounded-sm p-6" data-testid="admin-coats">
							<div className="flex gap-2 mb-6">
								<Input value={ newCoat } onChange={ e => setNewCoat(e.target.value) }
											 placeholder={ t('admin.coat_name') }
											 className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-10"
											 data-testid="new-coat-input" onKeyDown={ e => e.key === 'Enter' && addCoat() }/>
								<Button onClick={ addCoat }
												className="bg-[#1A3C34] hover:bg-[#153029] text-white rounded-sm h-10 px-6"
												data-testid="add-coat-btn">
									<Plus className="w-4 h-4 mr-1"/>
									{ t('admin.add_coat') }
								</Button>
							</div>
							<div className="flex flex-wrap gap-2">
								{ coats.map(b => (
									<div key={ b.id } className="flex items-center gap-2 bg-[#F2F0E9] px-3 py-2 rounded-sm"
											 data-testid={ `coat-tag-${ b.id }` }>
										<span className="text-sm text-[#1A3C34] font-medium">
											{ getTranslatedName(b) }
										</span>
										{ b.name !== getTranslatedName(b) &&
											<span className="text-[10px] text-[#666055]">
												({ b.name })
											</span>
										}
										<button onClick={ () => openCoatTranslations(b) }
														className="text-[#8B4513] hover:text-[#6F3610]"
														data-testid={ `edit-coat-${ b.id }` }>
											<Pencil className="w-3 h-3"/>
										</button>
										<button onClick={ () => deleteCoat(b.id) }
														className="text-[#9B2C2C] hover:text-red-700">
											<X className="w-3 h-3"/>
										</button>
									</div>
								)) }
							</div>
						</div>
					</TabsContent>

          {/* ===== CATEGORIES ===== */}
					<TabsContent value="categories">
						<div className="bg-white border border-[#E6E2D6] rounded-sm p-6" data-testid="admin-categories">
							<div className="flex gap-2 mb-6">
								<Input value={ newCategory } onChange={ e => setNewCategory(e.target.value) }
											 placeholder={ t('admin.category_name') }
											 className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-10" data-testid="new-category-input"
											 onKeyDown={ e => e.key === 'Enter' && addCategory() }/>
								<Button onClick={ addCategory }
												className="bg-[#8B4513] hover:bg-[#6F3610] text-white rounded-sm h-10 px-6"
												data-testid="add-category-btn">
									<Plus className="w-4 h-4 mr-1"/> { t('admin.add_category') }
								</Button>
							</div>
							<div className="flex flex-wrap gap-2">
								{ categories.map(c => (
									<div key={ c.id } className="flex items-center gap-2 bg-[#F2F0E9] px-3 py-2 rounded-sm"
											 data-testid={ `category-tag-${ c.id }` }>
										<span className="text-sm text-[#1A3C34] font-medium">{ getTranslatedName(c) }</span>
										{ c.name !== getTranslatedName(c) &&
											<span className="text-[10px] text-[#666055]">({ c.name })
											</span>
										}
										<button onClick={ () => openCatTranslations(c) }
														className="text-[#8B4513] hover:text-[#6F3610]"
														data-testid={ `edit-cat-${ c.id }` }>
											<Pencil className="w-3 h-3"/>
										</button>
										<button onClick={ () => deleteCategory(c.id) }
														className="text-[#9B2C2C] hover:text-red-700">
											<X className="w-3 h-3"/>
										</button>
									</div>
								)) }
							</div>
						</div>
					</TabsContent>

          {/* ===== SETTINGS ===== */}
          <TabsContent value="settings">
            <div className="space-y-6" data-testid="admin-settings">
              {/* Pricing */}
							<div className="bg-white border border-[#E6E2D6] rounded-sm p-6">
								<h3
									className="text-lg font-semibold text-[#1A3C34] font-['Playfair_Display'] mb-4 flex items-center gap-2">
									<EuroIcon className="w-5 h-5 text-[#8B4513]"/> { t('admin.pricing') }
								</h3>
								<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
									<SettingsField label={ t('admin.price_featured_ad') } value={ settingsForm.price_featured_ad }
																 onChange={ v => setSettingsForm({
																	 ...settingsForm,
																	 price_featured_ad: parseFloat(v) || 0
																 }) } suffix="EUR" testId="price-featured"/>
									<SettingsField label={ t('admin.price_extra_image') } value={ settingsForm.price_extra_image }
																 onChange={ v => setSettingsForm({
																	 ...settingsForm,
																	 price_extra_image: parseFloat(v) || 0
																 }) } suffix="EUR" testId="price-extra-image"/>
									<SettingsField label={ t('admin.price_extra_5_images') } value={ settingsForm.price_extra_5_images }
																 onChange={ v => setSettingsForm({
																	 ...settingsForm,
																	 price_extra_5_images: parseFloat(v) || 0
																 }) } suffix="EUR" testId="price-extra-5-images"/>
									<SettingsField label={ t('admin.price_extra_video') } value={ settingsForm.price_extra_video }
																 onChange={ v => setSettingsForm({
																	 ...settingsForm,
																	 price_extra_video: parseFloat(v) || 0
																 }) } suffix="EUR" testId="price-extra-video"/>
									<SettingsField label={ t('admin.price_seller_verification') }
																 value={ settingsForm.price_seller_verification } onChange={ v => setSettingsForm({
										...settingsForm,
										price_seller_verification: parseFloat(v) || 0
									}) } suffix="EUR" testId="price-verification"/>
									<SettingsField label={ t('admin.vat_percent') }
																 value={ settingsForm.vat_percent } onChange={ v => setSettingsForm({
										...settingsForm,
										vat_percent: parseFloat(v) || 0
									}) } suffix="%" testId="price-verification"/>
								</div>
							</div>
              {/* Media Limits */}
              <div className="bg-white border border-[#E6E2D6] rounded-sm p-6">
								<h3
									className="text-lg font-semibold text-[#1A3C34] font-['Playfair_Display'] mb-4 flex items-center gap-2">
									<Image className="w-5 h-5 text-[#8B4513]"/> { t('admin.media_limits') }</h3>
								<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
									<SettingsField label={ t('admin.free_images') } value={ settingsForm.free_images_limit }
																 onChange={ v => setSettingsForm({
																	 ...settingsForm,
																	 free_images_limit: parseInt(v) || 0
																 }) } testId="free-images"/>
									<SettingsField label={ t('admin.free_videos') } value={ settingsForm.free_videos_limit }
																 onChange={ v => setSettingsForm({
																	 ...settingsForm,
																	 free_videos_limit: parseInt(v) || 0
																 }) } testId="free-videos"/>
									<SettingsField label={ t('admin.max_video_size') } value={ settingsForm.max_video_size_mb }
																 onChange={ v => setSettingsForm({
																	 ...settingsForm,
																	 max_video_size_mb: parseInt(v) || 0
																 }) } suffix="MB" testId="max-video-size"/>
								</div>
              </div>
              {/* Company Info */}
							<div className="bg-white border border-[#E6E2D6] rounded-sm p-6">
								<h3
									className="text-lg font-semibold text-[#1A3C34] font-['Playfair_Display'] mb-4 flex items-center gap-2">
									<Building2 className="w-5 h-5 text-[#8B4513]"/> { t('admin.company') }</h3>
								<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
									<SettingsField label={ t('admin.company_name') } value={ settingsForm.company_name }
																 onChange={ v => setSettingsForm({ ...settingsForm, company_name: v }) }
																 testId="company-name" type="text"/>
									<SettingsField label={ t('admin.company_email') } value={ settingsForm.company_email }
																 onChange={ v => setSettingsForm({ ...settingsForm, company_email: v }) }
																 testId="company-email" type="text"/>
									<SettingsField label={ t('admin.company_phone') } value={ settingsForm.company_phone }
																 onChange={ v => setSettingsForm({ ...settingsForm, company_phone: v }) }
																 testId="company-phone" type="text"/>
									<SettingsField label={ t('admin.company_vat') } value={ settingsForm.company_vat }
																 onChange={ v => setSettingsForm({ ...settingsForm, company_vat: v }) }
																 testId="company-vat" type="text"/>
									<SettingsField label={ t('admin.company_address') } value={ settingsForm.company_address }
																 onChange={ v => setSettingsForm({ ...settingsForm, company_address: v }) }
																 testId="company-address" type="text"/>
									<SettingsField label={ t('admin.company_city') } value={ settingsForm.company_city }
																 onChange={ v => setSettingsForm({ ...settingsForm, company_city: v }) }
																 testId="company-city" type="text"/>
									<SettingsField label={ t('admin.company_country') } value={ settingsForm.company_country }
																 onChange={ v => setSettingsForm({ ...settingsForm, company_country: v }) }
																 testId="company-country" type="text"/>
									<SettingsField label={ t('admin.company_postal_code') } value={ settingsForm.company_postal_code }
																 onChange={ v => setSettingsForm({ ...settingsForm, company_postal_code: v }) }
																 testId="company-postal" type="text"/>
								</div>
							</div>
							<Button onClick={ saveSettings } disabled={ settingsSaving }
											className="bg-[#1A3C34] hover:bg-[#153029] text-white rounded-sm h-11 px-8"
											data-testid="save-settings-btn">
								<Save className="w-4 h-4 mr-2"/> { settingsSaving ? t('common.loading') : t('admin.save_changes') }
							</Button>
            </div>
          </TabsContent>

          {/* ===== REVENUE ===== */}
          <TabsContent value="revenue">
            <div className="space-y-6" data-testid="admin-revenue">
              {revenue ? (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-white border border-[#E6E2D6] rounded-sm p-6">
                      <p className="text-xs text-[#666055] uppercase tracking-wider mb-1">
												{t('admin.monthly_revenue')}
											</p>
                      <p className="text-3xl font-bold text-[#1A3C34]" data-testid="monthly-revenue">
												&euro;{revenue.monthly_revenue.toLocaleString()}
											</p>
                    </div>
                    <div className="bg-white border border-[#E6E2D6] rounded-sm p-6">
                      <p className="text-xs text-[#666055] uppercase tracking-wider mb-1">
												{t('admin.yearly_revenue')}
											</p>
                      <p className="text-3xl font-bold text-[#1A3C34]" data-testid="yearly-revenue">
												&euro;{revenue.yearly_revenue.toLocaleString()}
											</p>
                    </div>
                  </div>
                  {/* Revenue by type */}
									<div className="bg-white border border-[#E6E2D6] rounded-sm p-6">
										<h3
											className="text-lg font-semibold text-[#1A3C34] font-['Playfair_Display'] mb-4">
											{ t('admin.revenue_by_type') }
										</h3>
										<div className="grid grid-cols-2 md:grid-cols-4 gap-4">
											{ [
												{
													key: 'featured_ad',
													label: t('admin.featured_ads'),
													icon: Crown,
													color: 'bg-amber-100 text-amber-700'
												},
												{
													key: 'extra_images',
													label: t('admin.extra_images'),
													icon: Image,
													color: 'bg-blue-100 text-blue-700',
													includeKey: 'extra_5_images'
												},
												{
													key: 'extra_videos',
													label: t('admin.extra_videos'),
													icon: Video,
													color: 'bg-purple-100 text-purple-700'
												},
												{
													key: 'seller_verification',
													label: t('admin.seller_verifications'),
													icon: Shield,
													color: 'bg-green-100 text-green-700'
												},
											].map(item => (
												<div key={ item.key } className={ `rounded-sm p-4 ${ item.color }` }
														 data-testid={ `revenue-${ item.key }` }>
													<item.icon className="w-5 h-5 mb-2"/>
													<p className="text-xl font-bold">
														&euro;{ ((revenue.by_type[item.key] || 0) + (item.includeKey ? (revenue.by_type[item.includeKey] || 0) : 0)).toLocaleString() }
													</p>
													<p className="text-xs mt-1">{ item.label }</p>
												</div>
											)) }
										</div>
									</div>
                  {/* Monthly chart */}
									<div className="bg-white border border-[#E6E2D6] rounded-sm p-6">
										<h3
											className="text-lg font-semibold text-[#1A3C34] font-['Playfair_Display'] mb-4">
											{ t('admin.revenue_chart') }
										</h3>
										<div className="flex items-end gap-1 h-48" data-testid="revenue-chart">
											{ revenue.chart.map((m, i) => {
												const maxRev = Math.max(...revenue.chart.map(c => c.revenue), 1);
												const h = m.revenue > 0 ? Math.max((m.revenue / maxRev) * 100, 4) : 4;
												return (
													<div key={ i } className="flex-1 flex flex-col items-center justify-end gap-1">
														<span
															className="text-[9px] text-[#666055] font-medium">{ m.revenue > 0 ? `${ m.revenue }` : '' }
														</span>
														<div
															className={ `w-full rounded-t-sm transition-all ${ m.revenue > 0 ? 'bg-[#1A3C34]' : 'bg-[#E6E2D6]' }` }
															style={ { height: `${ h }%` } }/>
														<span className="text-[9px] text-[#666055]">{ m.month.split('-')[1] }</span>
													</div>
												);
											}) }
										</div>
                  </div>
                </>
              ) : (
                <div className="bg-white border border-[#E6E2D6] rounded-sm p-8 text-center">
									<p className="text-[#666055]">{t('admin.no_data')}</p>
								</div>
              )}
            </div>
          </TabsContent>

					{/* ===== ADS DISTRIBUTION ===== */ }
					<TabsContent value="distribution">
						<div className="bg-white border border-[#E6E2D6] rounded-sm p-6" data-testid="admin-distribution">
							<h3
								className="text-lg font-semibold text-[#1A3C34] font-['Playfair_Display'] mb-4 flex items-center gap-2">
								<MapPin className="w-5 h-5 text-[#8B4513]"/> { t('admin.ads_per_location') }
							</h3>
							{ adsDist.length > 0 ? (
								<div className="space-y-3">
									{ adsDist.map((d, i) => (
										<div key={ i } className="flex items-center gap-3" data-testid={ `dist-row-${ i }` }>
											<span className="text-sm text-[#1A3C34] w-40 truncate font-medium">{ d.location }</span>
											<div className="flex-1 bg-[#F2F0E9] rounded-sm h-6 overflow-hidden">
												<div
													className="h-full bg-[#1A3C34] rounded-sm transition-all flex items-center justify-end pr-2"
													style={ { width: `${ (d.count / maxBar) * 100 }%`, minWidth: '24px' } }>
													<span className="text-[10px] text-white font-bold">{ d.count }</span>
												</div>
											</div>
										</div>
									)) }
								</div>
							) : (
								<p className="text-[#666055] text-center py-8">{ t('admin.no_data') }</p>
							) }
						</div>
					</TabsContent>

          {/* ===== VERIFICATIONS ===== */}
          <TabsContent value="verifications">
            <div className="space-y-4" data-testid="admin-verifications">
              {verifications.length === 0 ? (
                <div className="bg-white border border-[#E6E2D6] rounded-sm p-8 text-center">
                  <Shield className="w-10 h-10 mx-auto mb-3 text-[#D4C5A9]" />
                  <p className="text-[#666055]">{t('admin.no_data')}</p>
                </div>
              ) : (
								verifications.map(ver => {
									const docs = (ver.documents || []).filter(d => d && typeof d === 'object');
									const legacyDocs = (ver.documents || []).filter(d => typeof d === 'string');
									const docLabel = (dt) => t(`verification.doc.${dt}.label`, dt);
									return (
									<div key={ ver.id } className="bg-white border border-[#E6E2D6] rounded-sm p-5"
											 data-testid={ `verification-${ ver.id }` }>
										<div className="flex items-start justify-between mb-3">
											<div>
												<h3 className="font-semibold text-[#1A3C34]">{ ver.user_name }</h3>
												<p className="text-xs text-[#666055]">{ ver.user_email }</p>
												{ ver.business_name && (
													<p className="text-xs text-[#1A3C34] mt-1">
														<span className="text-[#666055]">
															{t('verification.business_name')}:
														</span> {ver.business_name}
													</p>
												) }
												{ ver.vat_number && (
													<p className="text-xs text-[#1A3C34]">
														<span className="text-[#666055]">
															{t('verification.vat_number')}:
														</span> {ver.vat_number}
													</p>
												) }
											</div>
											<Badge
												className={ `rounded-sm text-[10px] ${ ver.status === 'approved' ? 'bg-green-100 text-green-700' 
													: ver.status === 'rejected' ? 'bg-red-100 text-[#9B2C2C]' : ver.status === 'pending' ? 
														'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-500' }` }>
												{ ver.status }
											</Badge>
										</div>

										{(ver.description || ver.credentials_description) && (
											<div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3 text-xs">
												{ver.description && (
													<div className="bg-[#F9F7F2] border border-[#E6E2D6] rounded-sm p-3">
														<p className="font-bold text-[#8B4513] uppercase tracking-wider mb-1">
															{t('verification.describe_business')}
														</p>
														<p className="text-[#3a3a3a] whitespace-pre-line">{ver.description}</p>
													</div>
												)}
												{ver.credentials_description && (
													<div className="bg-[#F9F7F2] border border-[#E6E2D6] rounded-sm p-3">
														<p className="font-bold text-[#8B4513] uppercase tracking-wider mb-1">
															{t('verification.credentials')}
														</p>
														<p className="text-[#3a3a3a] whitespace-pre-line">{ver.credentials_description}</p>
													</div>
												)}
											</div>
										)}

										<div className="mt-3">
											<p className="text-[11px] font-bold text-[#8B4513] uppercase tracking-wider mb-2">
												{t('verification.docs_title')} ({docs.length + legacyDocs.length})</p>
											{docs.length + legacyDocs.length === 0 ? (
												<p className="text-xs text-[#666055] italic">{t('verification.no_docs_uploaded')}</p>
											) : (
												<ul className="space-y-1" data-testid={`ver-admin-docs-${ver.id}`}>
													{docs.map(d => (
														<li key={d.id || d.url} className="flex items-center justify-between text-xs bg-[#F9F7F2] px-3 py-2 rounded-sm border border-[#E6E2D6]">
                              <span className="flex items-center gap-2 min-w-0">
                                <FileText className="w-3.5 h-3.5 text-[#8B4513] shrink-0" />
                                <span className="font-semibold text-[#1A3C34] shrink-0">{docLabel(d.doc_type)}</span>
                                <span className="text-[#666055] truncate">· {d.filename}</span>
                              </span>
															<button
																type="button"
																onClick={() => verificationAPI.viewDocument(d.url, d.filename)
																	.catch(() => toast.error(t('common.error')))}
																className="text-[#8B4513] hover:underline flex items-center gap-1 shrink-0"
																data-testid={`ver-admin-doc-view-${d.id || d.doc_type}`}
															>
																<ExternalLink className="w-3 h-3" /> {t('verification.view')}
															</button>
														</li>
													))}
													{legacyDocs.map((url, i) => (
														<li key={`legacy-${i}`} className="flex items-center justify-between text-xs bg-[#F9F7F2]
																px-3 py-2 rounded-sm border border-[#E6E2D6]">
                              <span className="flex items-center gap-2 min-w-0">
                                <FileText className="w-3.5 h-3.5 text-[#666055] shrink-0" />
                                <span className="text-[#666055]">{t('verification.doc.other.label')}</span>
                              </span>
															<button type="button" onClick={() => verificationAPI.viewDocument(url, 'document')
																		.catch(() => toast.error(t('common.error')))}
																			className="text-[#8B4513] hover:underline flex items-center gap-1 shrink-0">
																<ExternalLink className="w-3 h-3" /> {t('verification.view')}
															</button>
														</li>
													))}
												</ul>
											)}
										</div>

										{ ver.status === 'pending' && (
											<div className="flex gap-2 mt-3 pt-3 border-t border-[#E6E2D6]">
												<Button onClick={ () => reviewVerification(ver.id, 'approved') }
																className="bg-green-600 hover:bg-green-700 text-white rounded-sm h-9 text-xs"
																data-testid={ `approve-${ ver.id }` }>
													<CheckCircle className="w-3 h-3 mr-1"/>
													{ t('admin.approve') }
												</Button>
												<Button onClick={() => setRejectDialog({ open: true, id: ver.id, reason: '' })}
																variant="outline" className="border-red-200 text-[#9B2C2C] rounded-sm h-9 text-xs"
																data-testid={`reject-${ver.id}`}>
													<XCircle className="w-3 h-3 mr-1" />
													{t('admin.reject')}
												</Button>
											</div>
										) }
									</div>
									);
								})
              )}
            </div>
          </TabsContent>

					{/* ===== REPORTS ===== */}
					<TabsContent value="reports">
						<div className="space-y-4" data-testid="admin-reports-tab">
							<div className="bg-white border border-[#E6E2D6] rounded-sm p-4 flex items-center justify-between gap-4 flex-wrap">
								<div className="flex items-center gap-4 text-sm">
									<span className="flex items-center gap-2 text-[#9B2C2C] font-semibold"><Flag className="w-4 h-4" />
										{reportStats.open} {t('admin.report_open')}
									</span>
									<span className="text-[#666055]">{reportStats.resolved} {t('admin.report_resolved')}</span>
									<span className="text-[#666055]">{reportStats.dismissed} {t('admin.report_dismissed')}</span>
								</div>
								<div className="flex items-center gap-2">
									<span className="text-xs text-[#666055] uppercase tracking-wider">
										{t('admin.report_filter')}:
									</span>
									<Select value={reportFilter} onValueChange={changeReportFilter}>
										<SelectTrigger className="h-9 w-[140px] bg-[#F9F7F2] border-[#E6E2D6] rounded-sm"
																	 data-testid="admin-report-filter">
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="open">{t('admin.report_open')}</SelectItem>
											<SelectItem value="resolved">{t('admin.report_resolved')}</SelectItem>
											<SelectItem value="dismissed">{t('admin.report_dismissed')}</SelectItem>
											<SelectItem value="all">{t('admin.report_all')}</SelectItem>
										</SelectContent>
									</Select>
								</div>
							</div>

							{reports.length === 0 ? (
								<div className="bg-white border border-[#E6E2D6] rounded-sm p-12 text-center text-sm text-[#666055]">
									{t('admin.no_reports')}
								</div>
							) : (
								<div className="space-y-3">
									{reports.map((r) => {
										const reasonLabel = t(`report.reason.${r.reason}`);
										const created = r.created_at ? new Date(r.created_at).toLocaleString() : '';
										const targetUrl = r.target_type === 'ad'
											? `/${ resolveUrl(r.ad_kind) }/${r.target_slug || r.target_id}`
											: `/u/${r.target_slug || ''}`;
										return (
											<div
												key={r.id}
												className="bg-white border border-[#E6E2D6] rounded-sm p-4"
												data-testid={`admin-report-row-${r.id}`}
											>
												<div className="flex items-start gap-3 mb-3">
													<div className={`w-10 h-10 rounded-sm flex items-center justify-center flex-shrink-0 ${r.status === 'open' ? 'bg-[#FBEAEA] text-[#9B2C2C]' : 'bg-[#F2F0E9] text-[#666055]'}`}>
														<Flag className="w-5 h-5" />
													</div>
													<div className="min-w-0 flex-1">
														<div className="flex items-center gap-2 flex-wrap">
															<Badge variant={r.target_type === 'ad' ? 'default' : 'outline'}
																		 className={r.target_type === 'ad' ? 'bg-[#1A3C34] text-white' : 'border-[#8B4513] text-[#8B4513]'}>
																{r.target_type === 'ad' ? t(resolveLabel(r.ad_kind)) : t('admin.report_target_user')}
															</Badge>
															<span className="bg-[#FBEAEA] text-[#9B2C2C] px-2 py-0.5 rounded-sm text-[10px] uppercase
																	tracking-wider font-bold">{reasonLabel}</span>
															{r.status !== 'open' && (
																<span className="bg-[#F2F0E9] text-[#666055] px-2 py-0.5 rounded-sm text-[10px]
																		uppercase tracking-wider font-bold">{t(`admin.report_${r.status}`)}</span>
															)}
														</div>
														<h4 className="text-base font-semibold text-[#1A3C34] mt-2 truncate font-['Playfair_Display']">
															{r.target_label}
														</h4>
														<p className="text-xs text-[#666055] mt-0.5">{t('admin.report_by')}
															<strong>{r.reporter_name}</strong> · {created}
														</p>
														<p className="mt-2 text-sm text-[#3a3a3a] whitespace-pre-line">{r.details}</p>
														{r.admin_note && (
															<p className="mt-2 text-xs text-[#666055] italic"><AlertTriangle
																className="w-3 h-3 inline mr-1" /> {r.admin_note}</p>
														)}
													</div>
												</div>
												<div className="flex items-center gap-2 justify-end flex-wrap pt-2 border-t border-[#E6E2D6]">
													<a href={targetUrl} target="_blank" rel="noopener noreferrer" className="inline-flex
															items-center gap-1 text-xs text-[#8B4513] hover:text-[#6F3610] mr-auto"
														 data-testid={`admin-report-view-${r.id}`}>
														<ExternalLink className="w-3 h-3" /> {t('admin.report_view_target')}
													</a>
													{r.status === 'open' && (
														<>
															<Button
																size="sm"
																variant="outline"
																onClick={() => reportResolve(r.id, 'dismissed')}
																className="border-[#E6E2D6] text-[#666055] hover:bg-[#F2F0E9] rounded-sm h-8 text-xs"
																data-testid={`admin-report-dismiss-${r.id}`}
															>
																{t('admin.report_dismiss')}
															</Button>
															<Button
																size="sm"
																variant="outline"
																onClick={() => reportResolve(r.id, 'resolved')}
																className="border-[#E6E2D6] text-[#1A3C34] hover:bg-[#F2F0E9] rounded-sm h-8 text-xs"
																data-testid={`admin-report-resolve-${r.id}`}
															>
																<CheckCircle className="w-3 h-3 mr-1" /> {t('admin.report_resolve')}
															</Button>
															<Button
																size="sm"
																onClick={() => reportTakeAction(r.id)}
																className="bg-[#9B2C2C] hover:bg-[#7B1F1F] text-white rounded-sm h-8 text-xs"
																data-testid={`admin-report-action-${r.id}`}
															>
																{r.target_type === 'ad' ? t('admin.report_deactivate_ad') : t('admin.report_block_user')}
															</Button>
														</>
													)}
												</div>
											</div>
										);
									})}
								</div>
							)}
						</div>
					</TabsContent>

					{/* ===== RATING REPORTS ===== */}
					<TabsContent value="rating_reports">
						<div className="space-y-4" data-testid="admin-rating-reports-tab">
							<div className="bg-white border border-[#E6E2D6] rounded-sm p-4 flex items-center justify-between gap-4 flex-wrap">
								<div className="flex items-center gap-4 text-sm">
									<span className="flex items-center gap-2 text-[#9B2C2C] font-semibold"><Flag className="w-4 h-4" /> {ratingReportStats.open} {t('admin.report_open')}</span>
									<span className="text-[#666055]">{ratingReportStats.resolved} {t('admin.report_resolved')}</span>
									<span className="text-[#666055]">{ratingReportStats.dismissed} {t('admin.report_dismissed')}</span>
								</div>
								<div className="flex items-center gap-2">
									<span className="text-xs text-[#666055] uppercase tracking-wider">{t('admin.report_filter')}:</span>
									<Select value={ratingReportFilter} onValueChange={changeRatingReportFilter}>
										<SelectTrigger className="h-9 w-[140px] bg-[#F9F7F2] border-[#E6E2D6] rounded-sm" data-testid="admin-rating-report-filter">
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="open">{t('admin.report_open')}</SelectItem>
											<SelectItem value="resolved">{t('admin.report_resolved')}</SelectItem>
											<SelectItem value="dismissed">{t('admin.report_dismissed')}</SelectItem>
											<SelectItem value="all">{t('admin.report_all')}</SelectItem>
										</SelectContent>
									</Select>
								</div>
							</div>

							{ratingReports.length === 0 ? (
								<div className="bg-white border border-[#E6E2D6] rounded-sm p-12 text-center text-sm text-[#666055]">
									{t('admin.no_rating_reports')}
								</div>
							) : (
								<div className="space-y-3">
									{ratingReports.map((r) => {
										const created = r.created_at ? new Date(r.created_at).toLocaleString() : '';
										const rating = r.rating;
										return (
											<div key={r.id} className="bg-white border border-[#E6E2D6] rounded-sm p-4" data-testid={`admin-rating-report-row-${r.id}`}>
												<div className="flex items-start gap-3 mb-3">
													<div className={`w-10 h-10 rounded-sm flex items-center justify-center flex-shrink-0 ${r.status === 'open' ? 'bg-[#FBEAEA] text-[#9B2C2C]' : 'bg-[#F2F0E9] text-[#666055]'}`}>
														<Star className="w-5 h-5" />
													</div>
													<div className="min-w-0 flex-1">
														<div className="flex items-center gap-2 flex-wrap">
															<span className="bg-[#FBEAEA] text-[#9B2C2C] px-2 py-0.5 rounded-sm text-[10px] uppercase tracking-wider font-bold">{t(`rating.report_reason_${r.reason}`)}</span>
															{r.status !== 'open' && (
																<span className="bg-[#F2F0E9] text-[#666055] px-2 py-0.5 rounded-sm text-[10px] uppercase tracking-wider font-bold">{t(`admin.report_${r.status}`)}</span>
															)}
															{r.rating_removed && (
																<span className="bg-[#9B2C2C] text-white px-2 py-0.5 rounded-sm text-[10px] uppercase tracking-wider font-bold">{t('admin.rating_removed_badge')}</span>
															)}
														</div>
														<h4 className="text-base font-semibold text-[#1A3C34] mt-2 truncate font-['Playfair_Display']">
															{r.seller_slug ? (
																<a href={`/u/${r.seller_slug}`} target="_blank" rel="noopener noreferrer" className="hover:text-[#8B4513]">
																	{r.seller_name} <ExternalLink className="inline w-3 h-3 ml-1" />
																</a>
															) : r.seller_name}
														</h4>
														<p className="text-xs text-[#666055] mt-0.5">{t('admin.report_by')} <strong>{r.reporter_name}</strong> · {created}</p>
														{r.note && (
															<p className="mt-2 text-sm text-[#3a3a3a] whitespace-pre-line">{r.note}</p>
														)}
														{rating && (
															<div className="mt-3 border-l-2 border-[#8B4513] pl-3 bg-[#F9F7F2] py-2 rounded-sm">
																<div className="flex items-center gap-2 text-xs text-[#666055]">
																	<span className="font-semibold text-[#1A3C34]">{rating.rater_name}</span>
																	<span className="text-[#D4AF37]">{'★'.repeat(rating.stars)}{'☆'.repeat(5 - rating.stars)}</span>
																	<span>· {rating.stars}/5</span>
																</div>
																{rating.comment && (
																	<p className="text-sm text-[#3a3a3a] mt-1 whitespace-pre-line" data-testid={`admin-rating-comment-${r.id}`}>{rating.comment}</p>
																)}
															</div>
														)}
														{r.admin_note && (
															<p className="mt-2 text-xs text-[#666055] italic"><AlertTriangle className="w-3 h-3 inline mr-1" /> {r.admin_note}</p>
														)}
													</div>
												</div>
												<div className="flex items-center gap-2 justify-end flex-wrap pt-2 border-t border-[#E6E2D6]">
													{r.status === 'open' && (
														<>
															<Button
																size="sm"
																variant="outline"
																onClick={() => ratingReportDismiss(r.id)}
																className="border-[#E6E2D6] text-[#666055] hover:bg-[#F2F0E9] rounded-sm h-8 text-xs"
																data-testid={`admin-rating-report-dismiss-${r.id}`}
															>
																{t('admin.report_dismiss')}
															</Button>
															{!r.rating_removed && rating && (
																<Button
																	size="sm"
																	onClick={() => ratingReportRemoveRating(rating.id)}
																	className="bg-[#9B2C2C] hover:bg-[#7B1F1F] text-white rounded-sm h-8 text-xs"
																	data-testid={`admin-rating-report-remove-${r.id}`}
																>
																	<Trash2 className="w-3 h-3 mr-1" /> {t('admin.rating_remove_action')}
																</Button>
															)}
														</>
													)}
												</div>
											</div>
										);
									})}
								</div>
							)}
						</div>
					</TabsContent>

					{/* ===== FAQ MANAGEMENT ===== */}
					<TabsContent value="faqs">
						<FAQAdminManager />
					</TabsContent>
					{/* ===== SUGGESTIONS ===== */}
					<TabsContent value="suggestions">
						<SuggestionsAdminManager />
					</TabsContent>
					{/* ===== LEGAL DOCUMENTS ===== */}
					<TabsContent value="legal">
						<LegalAdminManager />
					</TabsContent>
					{/* ===== INBOX ===== */}
					<TabsContent value="inbox">
						<InboxAdminManager />
					</TabsContent>
        </Tabs>
      </div>

			{/* Reject Verification Reason Dialog */}
			<Dialog open={rejectDialog.open}
							onOpenChange={(o) => !o && setRejectDialog({ open: false, id: null, reason: '' })}>
				<DialogContent className="max-w-md" data-testid="reject-verification-dialog">
					<DialogHeader>
						<DialogTitle>{t('admin.reject_verification_title')}</DialogTitle>
					</DialogHeader>
					<div className="space-y-3 mt-2">
						<p className="text-xs text-[#666055]">{t('admin.reject_verification_hint')}</p>
						<Textarea
							autoFocus
							value={rejectDialog.reason}
							onChange={(e) => setRejectDialog(p => ({ ...p, reason: e.target.value }))}
							placeholder={t('admin.reject_verification_placeholder')}
							rows={5}
							maxLength={1000}
							className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm text-sm"
							data-testid="reject-verification-reason"
						/>
						<div className="flex justify-end gap-2 pt-2">
							<Button variant="ghost"
											onClick={() => setRejectDialog({ open: false, id: null, reason: '' })}
											className="text-[#666055]">
								{t('common.cancel')}
							</Button>
							<Button
								onClick={async () => {
									const reason = rejectDialog.reason.trim();
									if (!reason) { toast.error(t('admin.reject_verification_required')); return; }
									await reviewVerification(rejectDialog.id, 'rejected', reason);
									setRejectDialog({ open: false, id: null, reason: '' });
								}}
								className="bg-[#9B2C2C] hover:bg-[#7B2020] text-white rounded-sm"
								disabled={!rejectDialog.reason.trim()}
								data-testid="reject-verification-confirm"
							>
								<XCircle className="w-4 h-4 mr-1" /> {t('admin.reject')}
							</Button>
						</div>
					</div>
				</DialogContent>
			</Dialog>

      {/* Breed Translation Dialog */}
			<Dialog open={ !!editingBreed } onOpenChange={ () => setEditingBreed(null) }>
				<DialogContent className="max-w-md">
					<DialogHeader>
						<DialogTitle>
							{ t('admin.edit_translations') } - { editingBreed?.name }
						</DialogTitle>
					</DialogHeader>
					<div className="space-y-3 mt-2">
						{ LANGS.map(lang => (
							<div key={ lang } className="flex items-center gap-3">
								<span className="text-xs font-semibold text-[#666055] w-8 uppercase">{ lang }</span>
								<Input value={ lang === 'en' ? (editingBreed?.name || '') : (breedTranslations[lang] || '') }
											 onChange={ e => {
												 if (lang === 'en') return;
												 setBreedTranslations({ ...breedTranslations, [lang]: e.target.value });
											 } } disabled={ lang === 'en' } className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-9 text-sm"
											 placeholder={ editingBreed?.name } data-testid={ `breed-trans-${ lang }` }/>
							</div>
						)) }
						<label className="flex items-center gap-2 text-sm text-[#3a3a3a] cursor-pointer">
							<input type="checkbox" checked={ popularBreed }
										 onChange={e => setPopularBreed(e.target.checked) } />
							{t('home.popular_breed')}
						</label>
						<Button onClick={ saveBreedTranslations }
										className="w-full bg-[#1A3C34] hover:bg-[#153029] text-white rounded-sm h-10"
										data-testid="save-breed-translations">
							<Save className="w-4 h-4 mr-2"/> { t('admin.save_changes') }
						</Button>
					</div>
				</DialogContent>
			</Dialog>

			{/* Coat Translation Dialog */}
			<Dialog open={ !!editingCoat } onOpenChange={ () => setEditingCoat(null) }>
				<DialogContent className="max-w-md">
					<DialogHeader>
						<DialogTitle>
							{ t('admin.edit_translations') } - { editingCoat?.name }
						</DialogTitle>
					</DialogHeader>
					<div className="space-y-3 mt-2">
						{ LANGS.map(lang => (
							<div key={ lang } className="flex gap-3">
								<span className="mt-2 text-xs font-semibold text-[#666055] w-8 uppercase">{ lang }</span>
								<div className="w-full">
								<ul>
									<Input value={ lang === 'en' ? (editingCoat?.name || '') : (coatTranslations[lang] || '') }
												 onChange={ e => {
													 if (lang === 'en') return;
													 setCoatTranslations({ ...coatTranslations, [lang]: e.target.value });
												 } } disabled={ lang === 'en' } className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-9 text-sm"
												 placeholder={ editingCoat?.name } data-testid={ `coat-trans-${ lang }` }/>
								</ul>
								<ul>
									<Textarea data-testid="coat-description" value={  (coatDescriptionTranslations[lang] || '') }
														onChange={ e => setCoatDescriptionTranslations({ ...coatDescriptionTranslations, [lang]: e.target.value }) }
														className="mt-2 bg-[#F9F7F2] border-[#E6E2D6] rounded-sm min-h-[60px]"
														placeholder={ `Enter coat description in ${ lang }` } />
								</ul>
								</div>
							</div>
						)) }
						<Button onClick={ saveCoatTranslations }
										className="w-full bg-[#1A3C34] hover:bg-[#153029] text-white rounded-sm h-10"
										data-testid="save-coat-translations">
							<Save className="w-4 h-4 mr-2"/> { t('admin.save_changes') }
						</Button>
					</div>
				</DialogContent>
			</Dialog>

      {/* Category Translation Dialog */}
      <Dialog open={!!editingCategory} onOpenChange={() => setEditingCategory(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
						<DialogTitle>
							{t('admin.edit_translations')} - {editingCategory?.name}
						</DialogTitle>
					</DialogHeader>
          <div className="space-y-3 mt-2">
            {LANGS.map(lang => (
              <div key={lang} className="flex items-center gap-3">
                <span className="text-xs font-semibold text-[#666055] w-8 uppercase">{lang}</span>
                <Input value={lang === 'en' ? (editingCategory?.name || '') : (catTranslations[lang] || '')}
											 onChange={e => { if (lang === 'en') return; setCatTranslations({...catTranslations, [lang]: e.target.value}); }}
											 disabled={lang === 'en'}
											 className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-9 text-sm"
											 placeholder={editingCategory?.name} data-testid={`cat-trans-${lang}`} />
              </div>
            ))}
            <Button onClick={saveCatTranslations}
										className="w-full bg-[#8B4513] hover:bg-[#6F3610] text-white rounded-sm h-10"
										data-testid="save-cat-translations">
							<Save className="w-4 h-4 mr-2" /> {t('admin.save_changes')}
						</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SettingsField({ label, value, onChange, suffix, testId, type = 'number' }) {
  return (
    <div>
      <label className="text-xs text-[#666055] font-medium mb-1 block">{label}</label>
      <div className="relative">
        <Input type={type} value={value ?? ''} onChange={e => onChange(e.target.value)}
							 className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-10 text-sm pr-12" data-testid={testId} />
        { suffix &&
					<span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#666055]">
						{suffix}
					</span>
				}
      </div>
    </div>
  );
}

function AdTable({ ads, type, onDeactivate, onReactivate, onDeleteAd, onDeletePendingAd, onMarkAsFeatured, onMarkAsStandard, t }) {
  return (
    <div className="bg-white border border-[#E6E2D6] rounded-sm overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-[#F2F0E9]">
          <tr>
            <th className="text-left p-3 text-xs font-semibold text-[#666055] uppercase">{t('admin.title_col')}</th>
            <th className="text-left p-3 text-xs font-semibold text-[#666055] uppercase">{t('admin.seller')}</th>
            <th className="text-left p-3 text-xs font-semibold text-[#666055] uppercase">{t('admin.price')}</th>
            <th className="text-left p-3 text-xs font-semibold text-[#666055] uppercase">{t('admin.status')}</th>
            <th className="text-left p-3 text-xs font-semibold text-[#666055] uppercase">{t('admin.actions')}</th>
          </tr>
        </thead>
        <tbody>
          {ads.map(ad => (
            <tr key={ad.id} className="border-t border-[#E6E2D6]">
              <td className="p-3 font-medium text-[#1A3C34] whitespace-nowrap">{ad.title}</td>
              <td className="p-3 text-[#666055] whitespace-nowrap">{ad.user_name}</td>
              <td className="p-3 text-[#8B4513] font-bold">&euro;{ad.price?.toLocaleString()}</td>
              <td className="p-3">
								<Badge className={`rounded-sm text-[10px] ${ad.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
									{ad.status === 'active' ? t('admin.active') : t('admin.blocked')}
								</Badge>
							</td>
              <td className="p-3 whitespace-nowrap">
                {ad.status === 'active' ? (
									<Button onClick={ () => onDeactivate(ad.id, type) } variant="outline" size="sm"
													className="rounded-sm h-7 text-xs border-[#E6E2D6] text-[#9B2C2C]"
													data-testid={ `admin-deactivate-${ ad.id }` }>
										<PowerOff className="w-3 h-3 mr-1"/> { t('admin.deactivate') }
									</Button>
                ) : (
                  <Button onClick={() => onReactivate(ad.id, type)} variant="outline" size="sm"
													className="rounded-sm h-7 text-xs border-[#E6E2D6] text-green-700"
													data-testid={`admin-activate-${ad.id}`}>
                    <Power className="w-3 h-3 mr-1" /> {t('admin.activate')}
                  </Button>
								) }
								{ ad.pendingPayment ? (
									<Button onClick={() => onDeletePendingAd(ad.id)} variant="outline" size="sm"
													className="rounded-sm h-7 text-xs border-[#E6E2D6] text-[#9B2C2C] ml-2"
													data-testid={`admin-delete-${ad.id}`}>
										<Trash2 className="w-3 h-3 mr-1" />
										{t('common.cancel_payment')}
									</Button>
								) : (
									<Button onClick={() => onDeleteAd(ad.id)} variant="outline" size="sm"
													className="rounded-sm h-7 text-xs border-[#E6E2D6] text-[#9B2C2C] ml-2"
													data-testid={`admin-delete-${ad.id}`}>
										<Trash2 className="w-3 h-3 mr-1" />
										{t('common.delete')}
									</Button>
								) }
								{ ad.is_featured ? (
									<Button onClick={() => onMarkAsStandard(ad.id, type)} variant="outline" size="sm"
													className="rounded-sm h-7 text-xs border-[#E6E2D6] text-[#9B2C2C] ml-2"
													data-testid={`admin-deactivate-${ad.id}`}>
										<ZapOff className="w-3 h-3 mr-1" /> {t('admin.mark_standard')}
									</Button>
								) : (
									<Button onClick={() => onMarkAsFeatured(ad.id, type)} variant="outline" size="sm"
													className="rounded-sm h-7 text-xs border-[#E6E2D6] text-green-700 ml-2"
													data-testid={`admin-activate-${ad.id}`}>
										<Zap className="w-3 h-3 mr-1" /> {t('admin.mark_featured')}
									</Button>
								)	}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
